/**
 * 작성자 : 김광민
 * 날짜 : 2024.01.24
 * 경로 : 구역별 상황판 > 터널 종합 진행 현황
 */

import { useQuery } from '@tanstack/react-query';
import { ERROR } from '../../_constants';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { PulseLoader } from 'react-spinners';
import styled from 'styled-components';
import TunnelTotalInfoMiniPoint from './TunnelTotalInfoMiniPoint';
import { apiGet } from '../../services/_common';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  user-select: none;
  overflow: hidden;
  > .title {
    height: 2rem;
    width: 100%;
    padding: 0 1rem;
    margin-top: 0.75rem;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    justify-content: space-between;
    .name {
      color: var(--text-primary);
      font-weight: var(--font-semibold);
      font-size: 1.125rem;
      line-height: 1.2;
    }
    .attend {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      .material-symbols-rounded {
        font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24;
        font-size: 0.875rem;
        color: var(--text-tertiary);
      }
      .valueName {
        font-size: 0.75rem;
        color: var(--text-tertiary);
      }
      .seperator {
        border-left: 1px solid var(--outline);
        height: 1rem;
      }
      .count {
        font-size: 0.875rem;
        color: var(--primary);
        padding: 0 0.25rem;
        border-radius: 0.25rem;
        background-color: var(--tonal-blue);
        min-width: 1.5rem;
        text-align: center;
      }
    }
  }
  > .content {
    width: 100%;
    flex-grow: 1;
    padding: 0 0.5rem;
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }
`;

type Props = { userInfo: any; tatCd: string; refetchInterval: number };

const TunnelTotalInfoMini = ({ userInfo, tatCd, refetchInterval }: Props) => {
  const { t } = useTranslation();
  const { hCd, sCd } = userInfo;

  // Tarea 데이터 요청 함수
  const fetchTareaData = async () => {
    try {
      const req = { hCd, sCd, tatCd };
      const res = await apiGet({ path: `/tarea/t/total`, req });
      const { data } = res.data || {};
      return data?.tareaList[0] || [];
    } catch (error) {
      console.error('API 호출 중 오류 발생:', error);
      toast.error(t(ERROR));
      throw new Error('API 호출 중 오류 발생');
    }
  };

  // Tarea 데이터 요청 상태값
  const { data, isError, isLoading } = useQuery(['TunnelTotalInfoMini', hCd, sCd], () => fetchTareaData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000' && !!tatCd,
    refetchInterval,
  });

  // 시점 및 종점 렌더링 함수
  const renderPoint = (point: 'start' | 'end') => {
    const { sUseYn, eUseYn } = data;
    const useYn = point === 'start' ? sUseYn : eUseYn;

    if (useYn === 'N') return null;

    return <TunnelTotalInfoMiniPoint point={point} data={data} />;
  };

  // 로딩 중일 때
  if (isLoading) {
    return (
      <div className='centered-content'>
        <PulseLoader color='rgb(0, 122, 255)' size='10px' />
      </div>
    );
  }

  // 오류가 발생했을 때
  if (isError) {
    console.error('API 호출 중 오류 발생:', data);
  }

  return (
    <Root>
      <div className='title'>
        <span className='name'>{t('터널 종합 진행 현황')}</span>
        <div className='attend'>
          <span className='material-symbols-rounded'>familiar_face_and_zone</span>
          <span className='valueName'>{t('출역인원')}</span>
          <span className='seperator' />
          <span className='count'>{data?.attendCount}</span>
        </div>
      </div>
      <div className='content'>
        {renderPoint('start')}
        {renderPoint('end')}
      </div>
    </Root>
  );
};

export default TunnelTotalInfoMini;
